import React, { useEffect, useMemo, useState } from "react";
import ResearchLayout, { useReveal } from "../ResearchLayout";
import makeRequest from "../../../makeRequest";

const TABS = ["All", "Ongoing", "Completed", "Closed"];

export default function ClinicalTrials() {
  useReveal();
  const [tab, setTab] = useState("All");
  const [loading, setLoading] = useState(true);
  const [trials, setTrials] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await makeRequest({ route: "/website/research-clinical-trial/public", method: "get" });
        if (mounted) setTrials(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        console.error("Failed to load trials:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => trials.filter((t) => tab === "All" || (t.status || "").toLowerCase() === tab.toLowerCase()), [trials, tab]);

  return (
    <ResearchLayout>
      <section
        style={{
          padding: "100px 0 60px",
          background: "linear-gradient(135deg, #0d3b6e 0%, #076cec 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="rs-blob" style={{ width: 400, height: 400, background: "#04b3bf", top: "-150px", right: "10%" }} />
        <div
          className="rs-blob"
          style={{ width: 300, height: 300, background: "#7c3aed", bottom: "-100px", left: "5%", animationDelay: "2s" }}
        />
        <div className="rs-container rs-text-center" style={{ position: "relative", zIndex: 2 }}>
          <span className="rs-section-eyebrow" style={{ background: "rgba(255,255,255,0.15)", color: "#fff", backdropFilter: "blur(10px)" }}>
            Studies & Investigations
          </span>
          <h1 style={{ fontSize: "clamp(2rem, 5vw, 3.4rem)", fontWeight: 800, margin: "12px 0 16px" }}>Clinical Trials</h1>
          <p style={{ fontSize: "1.05rem", maxWidth: 720, margin: "0 auto", opacity: 0.85, lineHeight: 1.7 }}>
            A portfolio of internationally sponsored clinical trials across cardiology, hepatology, neurology, virology, and pediatric medicine.
          </p>
        </div>
      </section>

      <section className="rs-section" style={{ paddingTop: 60 }}>
        <div className="rs-container">
          {loading ? (
            <div style={{ padding: "60px 20px", textAlign: "center", color: "#6b7280" }}>
              <i className="fa-regular fa-spinner fa-spin" style={{ fontSize: "1.8rem", color: "var(--rs-primary)", marginBottom: 12 }}></i>
              <div>Loading clinical trials…</div>
            </div>
          ) : trials.length === 0 ? (
            <div
              style={{
                padding: "50px 30px",
                textAlign: "center",
                background: "linear-gradient(135deg, #f0f7ff, #e6fffa)",
                borderRadius: 18,
                border: "1px dashed rgba(7, 108, 236,0.2)",
              }}
            >
              <h3 style={{ margin: "0 0 8px", color: "var(--rs-dark)", fontWeight: 800 }}>No trials yet</h3>
              <p style={{ color: "#6b7280", margin: 0 }}>Add Clinical Trials from the admin panel to populate this page.</p>
            </div>
          ) : (
            <>
              {/* Filter tabs */}
              <div
                className="rs-reveal"
                style={{
                  display: "flex",
                  gap: 10,
                  marginBottom: 32,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                {TABS.map((t) => {
                  const count = t === "All" ? trials.length : trials.filter((x) => (x.status || "").toLowerCase() === t.toLowerCase()).length;
                  return (
                    <button
                      key={t}
                      onClick={() => setTab(t)}
                      style={{
                        padding: "10px 22px",
                        borderRadius: 50,
                        border: "2px solid",
                        borderColor: tab === t ? "var(--rs-primary)" : "rgba(7, 108, 236,0.15)",
                        background: tab === t ? "var(--rs-primary)" : "#fff",
                        color: tab === t ? "#fff" : "var(--rs-primary)",
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        transition: "all 0.25s",
                      }}
                    >
                      {t} <span style={{ opacity: 0.7, marginLeft: 4 }}>({count})</span>
                    </button>
                  );
                })}
              </div>

              <div style={{ display: "grid", gap: 20 }}>
                {filtered.map((t, i) => (
                  <TrialCard key={t.id} t={t} index={i} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </ResearchLayout>
  );
}

function TrialCard({ t, index }) {
  const status = (t.status || "Ongoing").trim();
  const statusKey = status.toLowerCase();
  const accent =
    statusKey === "ongoing"
      ? "linear-gradient(180deg, #3b82f6, #1d4ed8)"
      : statusKey === "completed"
        ? "linear-gradient(180deg, #10b981, #047857)"
        : "linear-gradient(180deg, #f59e0b, #b45309)";

  /* PI list: split by newline, semicolon, or pipe to support free-form admin entry. */
  const pis = String(t.principalInvestigator || "")
    .split(/\r?\n|;|\|/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div
      className={`rs-reveal delay-${(index % 4) + 1}`}
      style={{
        background: "#fff",
        borderRadius: 18,
        overflow: "hidden",
        border: "1px solid rgba(7, 108, 236,0.08)",
        transition: "all 0.3s",
        display: "grid",
        gridTemplateColumns: "8px 1fr",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 20px 40px rgba(7, 108, 236,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{ background: accent }} />
      <div style={{ padding: "26px 30px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 800, color: "var(--rs-dark)" }}>{t.name}</h3>
          <span className={`rs-status rs-status-${statusKey}`}>{status}</span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 18,
            fontSize: "0.875rem",
          }}
        >
          {pis.length > 0 && (
            <div>
              <div style={fieldLabelStyle}>
                <i className="fa-regular fa-user-doctor" style={{ marginRight: 6 }}></i>
                Investigators
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", color: "#374151", lineHeight: 1.6 }}>
                {pis.map((p, j) => (
                  <li key={j}>{p}</li>
                ))}
              </ul>
            </div>
          )}
          {t.sponsor && (
            <div>
              <div style={fieldLabelStyle}>
                <i className="fa-regular fa-building-columns" style={{ marginRight: 6 }}></i>
                Sponsor
              </div>
              <div style={{ color: "#374151", lineHeight: 1.6 }}>{t.sponsor}</div>
            </div>
          )}
          {t.participants && (
            <div>
              <div style={fieldLabelStyle}>
                <i className="fa-regular fa-users" style={{ marginRight: 6 }}></i>
                Participants
              </div>
              <div style={{ color: "#374151", lineHeight: 1.6 }}>{t.participants}</div>
            </div>
          )}
        </div>

        {(t.nbcApproval || t.drapApproval || t.nctIdentifier) && (
          <div
            style={{
              marginTop: 18,
              paddingTop: 16,
              borderTop: "1px dashed #e5e7eb",
              display: "flex",
              gap: 18,
              flexWrap: "wrap",
              fontSize: "0.78rem",
              color: "#6b7280",
            }}
          >
            {t.nbcApproval && (
              <span>
                <strong style={{ color: "var(--rs-primary)" }}>NBC:</strong> {t.nbcApproval}
              </span>
            )}
            {t.drapApproval && (
              <span>
                <strong style={{ color: "var(--rs-primary)" }}>DRAP:</strong> {t.drapApproval}
              </span>
            )}
            {t.nctIdentifier && t.nctIdentifier !== "N/A" && (
              <a
                href={`https://clinicaltrials.gov/study/${t.nctIdentifier}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--rs-secondary)", fontWeight: 700, textDecoration: "none" }}
              >
                <i className="fa-regular fa-arrow-up-right-from-square" style={{ marginRight: 4 }}></i>
                {t.nctIdentifier}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const fieldLabelStyle = {
  color: "#9ca3af",
  fontWeight: 700,
  fontSize: "0.7rem",
  textTransform: "uppercase",
  marginBottom: 6,
  letterSpacing: "0.06em",
};
